import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Container,
  Row,
  Col,
  Card,
  CardTitle,
  CardBody,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { BeatLoader } from 'react-spinners';
import {
  getConversionRatesData,
  addConversionRatesData,
  getEftCategoriesData,
  removeConversionRatesData,
  setEndDateConversionRatesData,
  resetErrorMessages,
} from '../../actions/wwConversionRateActions';
import DatePicker from 'react-datepicker';
import ConversionRateRow from '../../components/conversionRate/conversionRateRow';
import NoRowExistsRow from '../../components/common/noRowExistsRow';
import { ToastContainer, toast } from 'react-toastify';

const WWConversionRatePage = () => {
  const dispatch = useDispatch();
  const eftCategoriesList = useSelector((state) => state.eftCategoriesListData);
  const conversionRateList = useSelector(
    (state) => state.conversionRateListData,
  );
  const isLoading = useSelector(
    (state) =>
      state.eftCategoriesListIsLoading ||
      state.conversionRateListIsLoading ||
      state.conversionRateAddIsLoading ||
      state.conversionRateDeleteIsLoading,
  );
  const errorMessage = useSelector(
    (state) =>
      state.conversionRateAddHasErrored.errorMessage ||
      state.conversionRateDeleteHasErrored.errorMessage ||
      state.conversionRateSetEndDateHasErrored.errorMessage ||
      '',
  );
  const isError = useSelector(
    (state) =>
      state.conversionRateAddHasErrored.hasErrored ||
      state.conversionRateDeleteHasErrored.hasErrored ||
      state.conversionRateSetEndDateHasErrored.hasErrored,
  );

  const [emptyRowString] = useState('No data exist');
  const [isFormValid, setIsFormValid] = useState(false);
  const [startDate, setStartDate] = useState(moment().add(1, 'days'));
  const [eftCatgory, setEftCatgory] = useState('');
  const [baseMultiplier, setBaseMultiplier] = useState('0.0');

  useEffect(() => {
    dispatch(getConversionRatesData());
    dispatch(getEftCategoriesData({}));
  }, [dispatch]);

  useEffect(() => {
    if (isError && errorMessage) {
      toast.error(errorMessage, { autoClose: 5000, position: 'bottom-left' });
    }
  }, [isError, errorMessage]);

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, eftCatgory, baseMultiplier]);

  const clearForm = useCallback(() => {
    setStartDate(moment().add(1, 'days'));
    setEftCatgory('');
    setBaseMultiplier('0.0');
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === 'eftCatgory') setEftCatgory(value);
    if (name === 'baseMultiplier') setBaseMultiplier(value);
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const validateForm = () => {
    const isSDateEmpty = !startDate;
    const isEftCatgoryEmpty = !eftCatgory;
    const isBaseMultiplierEmpty = !baseMultiplier || baseMultiplier < 0.0001;
    const valid = !isSDateEmpty && !isEftCatgoryEmpty && !isBaseMultiplierEmpty;
    setIsFormValid(valid);
    return valid;
  };

  const handleSubmitAddForm = (e) => {
    e.preventDefault();
    dispatch(resetErrorMessages());
    const conversionRate = {
      startDate: startDate.format('DD-MMM-YYYY'),
      eftCategoryId: eftCatgory,
      baseMultiplier: baseMultiplier,
    };
    dispatch(addConversionRatesData(conversionRate));
    clearForm();
  };

  const setEndDateForConversionRate = (id, endDate) => {
    dispatch(setEndDateConversionRatesData(id, endDate));
  };

  const handleDeleteRate = (rate, id) => {
    dispatch(resetErrorMessages());
    dispatch(removeConversionRatesData(id));
  };

  const getConversionRateRows = (categoryId) => {
    const shopConversionRateList = conversionRateList.filter(
      (rate) => rate.eftCategoryId === categoryId,
    );
    return shopConversionRateList.length > 0 ? (
      shopConversionRateList.map((conversionRate) => (
        <ConversionRateRow
          key={conversionRate.id}
          conversionRate={conversionRate}
          removeConversionRate={handleDeleteRate}
          selectedCategory={categoryId}
          setEndDateForConversionRate={setEndDateForConversionRate}
        />
      ))
    ) : (
      <NoRowExistsRow emptyRowText={emptyRowString} />
    );
  };

  const headerRow = (
    <Row>
      <Col md='4' className='column-header white-border'>
        Start Date
      </Col>
      <Col md='4' className='column-header white-border'>
        End Date
      </Col>
      <Col md='4' className='column-header white-border'>
        Rate
      </Col>
    </Row>
  );

  return (
    <div>
      <ToastContainer toastClassName='ppcToastContainer' />
      <Row>
        <Col md='3'>
          <h4>Manage Conversion Rates</h4>
        </Col>
        <Col md='1'>
          <BeatLoader color={'#004C5F'} loading={isLoading} />
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <CardBody>
              <CardTitle>Add New Points to Dollar Conversion Rate</CardTitle>
              <Container fluid>
                <Row className='w-100'>
                  <Form
                    className='d-flex align-items-center justify-content-left flex-nowrap w-100 gap-2  px-0'
                    onSubmit={handleSubmitAddForm}
                  >
                    <FormGroup className='d-flex align-items-center mb-0 flex-nowrap'>
                      <Label for='eftCatgory' className='mb-0 me-2 text-nowrap'>
                        EFT Category
                      </Label>
                      <Input
                        type='select'
                        name='eftCatgory'
                        onChange={handleInputChange}
                        value={eftCatgory}
                        required
                        style={{ minWidth: 180 }}
                      >
                        <option value='' disabled>
                          Select Category
                        </option>
                        {eftCategoriesList.map((ot) => (
                          <option key={ot.id} value={ot.id}>
                            {ot.name}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                    <FormGroup className='d-flex align-items-center mb-0 flex-nowrap'>
                      <Label for='startDate' className='mb-0 me-2 text-nowrap'>
                        Start Date
                      </Label>
                      <DatePicker
                        id='startDate'
                        name='startDate'
                        required
                        selected={startDate}
                        dateFormat='DD-MMM-YYYY'
                        onChange={handleStartDateChange}
                        className='form-control'
                        locale='en-au'
                        style={{ minWidth: 140 }}
                      />
                    </FormGroup>
                    <FormGroup className='d-flex align-items-center mb-0 flex-nowrap'>
                      <Label
                        for='baseMultiplier'
                        className='mb-0 me-2 text-nowrap'
                      >
                        Base Multiplier
                      </Label>
                      <Input
                        id='baseMultiplier'
                        name='baseMultiplier'
                        value={baseMultiplier}
                        onChange={handleInputChange}
                        className='form-control'
                        type='number'
                        min='0.0001'
                        max='100'
                        step='0.0001'
                        pattern='(\.\d{1,4})?$'
                        style={{ minWidth: 120 }}
                      />
                    </FormGroup>
                    <FormGroup className='d-flex align-items-center mb-0 flex-nowrap'>
                      <Button
                        color='primary'
                        name='btnAdd'
                        id='btnAdd'
                        disabled={!isFormValid || isLoading}
                        type='submit'
                      >
                        Add
                      </Button>
                    </FormGroup>
                  </Form>
                </Row>
              </Container>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <br />
      <Row>
        <Col>
          <Card>
            <Row>
              <Col>
                <CardBody>
                  <CardTitle>Default</CardTitle>
                  <Container fluid>
                    {headerRow}
                    {getConversionRateRows('DEFAULT')}
                  </Container>
                </CardBody>
              </Col>
              <Col>
                <CardBody>
                  <CardTitle>Shop</CardTitle>
                  <Container fluid>
                    {headerRow}
                    {getConversionRateRows('SHOP')}
                  </Container>
                </CardBody>
              </Col>
            </Row>
            <Row>
              <Col>
                <CardBody>
                  <CardTitle>Premium</CardTitle>
                  <Container fluid>
                    {headerRow}
                    {getConversionRateRows('PREMIUM')}
                  </Container>
                </CardBody>
              </Col>
              <Col>
                <CardBody>
                  <CardTitle>Standard</CardTitle>
                  <Container fluid>
                    {headerRow}
                    {getConversionRateRows('STANDARD')}
                  </Container>
                </CardBody>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default WWConversionRatePage;
